import React, { useEffect } from 'react';
import ptBR from 'antd/es/locale/pt_BR';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useSelector } from 'react-redux';
import { Empty, ConfigProvider } from 'antd';
import { AuthContextProvider } from '@unicus/react-ui-kit';
import getDataFromStorage from 'util/getDataFromStorage';
import { Routes } from 'components/routes';
import { GoogleOAuthProvider } from '@react-oauth/google';
import useConfig from './config';

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK
} from './constants/themeSettings';
import 'assets/vendors/style';
import 'styles/wieldy.less';

moment.locale('pt-br');
moment.updateLocale('pt-br', {
  weekdaysMin: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
});

const customizeRenderEmpty = () => (
  <Empty
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    description="Nenhum registro para exibir"
  />
);

const MainFunction = () => {
  const refreshToken = useSelector(({ auth = {} }) => auth?.refreshToken);
  const config = useConfig();
  const { themeType, layoutType, navStyle } = useSelector(
    ({ settings = {} }) => settings
  );

  const handleBeforeRefreshToken = (authData) => {
    return {
      refreshToken: authData.refreshToken,
      type: 'professional'
    };
  };

  const setLayoutType = (layoutType) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('full-layout');
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove('full-layout');
      document.body.classList.remove('framed-layout');
      document.body.classList.add('boxed-layout');
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove('boxed-layout');
      document.body.classList.remove('full-layout');
      document.body.classList.add('framed-layout');
    }
  };

  const setNavStyle = (navStyle) => {
    if (
      navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
      navStyle === NAV_STYLE_DARK_HORIZONTAL ||
      navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      navStyle === NAV_STYLE_ABOVE_HEADER ||
      navStyle === NAV_STYLE_BELOW_HEADER
    ) {
      document.body.classList.add('full-scroll');
      document.body.classList.add('horizontal-layout');
    } else {
      document.body.classList.remove('full-scroll');
      document.body.classList.remove('horizontal-layout');
    }
  };

  const onFocus = () => {
    const { expirationDate } = getDataFromStorage('auth_data');

    if (expirationDate) {
      const expiration = moment(expirationDate);
      const base = moment();

      if (expiration.isBefore(base)) {
        window.location.reload();
      }
    }

    const currentRefreshToken = localStorage.getItem('auth_refreshtoken');

    if (currentRefreshToken && refreshToken !== currentRefreshToken) {
      window.location.reload();
    }
  };

  useEffect(() => {
    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add('dark-theme');
    }

    setLayoutType(layoutType);
    setNavStyle(navStyle);
  }, [themeType, layoutType, navStyle]);

  return (
    <ConfigProvider renderEmpty={customizeRenderEmpty} locale={ptBR}>
      <AuthContextProvider
        enableRefreshToken
        onBeforeRefreshToken={handleBeforeRefreshToken}
      >
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
          <Routes
            routes={config.routes}
            isContainer={config.isContainer}
            restrictRoles={config.roles}
          />
        </GoogleOAuthProvider>
      </AuthContextProvider>
    </ConfigProvider>
  );
};

export default MainFunction;
